import * as SelectPrimitive from '@radix-ui/react-select'
import { type VariantProps, cva } from 'class-variance-authority'
import { Check, ChevronDown, X } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { Button } from '@/components/lemmala-design-system/button/button'
import { CheckboxWithLabel } from '@/components/lemmala-design-system/checkbox/checkbox'
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/lemmala-design-system/command/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/lemmala-design-system/popover/popover'
import { Tag, TagsList } from '@/components/lemmala-design-system/tag/tag'

interface SelectOption {
  value: string
  label: string
  disabled?: boolean
  description?: React.ReactNode
}

interface SelectContextValue {
  multi?: boolean
  selectedItems?: string[]
  onMultiValueChange?: (values: string[]) => void
  onTagRemove?: (id: string) => void
  onClear?: () => void
  clearable?: boolean
  error?: boolean
  disabled?: boolean
  iconLeft?: React.ReactNode
  inputSize?: 'default' | 'large'
  placeholder?: string
  options?: SelectOption[]
}

const SelectContext = React.createContext<SelectContextValue | null>(null)

const selectTriggerVariants = cva(
  'flex items-center rounded border bg-main-background-card text-body-default text-main-foreground-primary px-2 outline-none ring-offset-main-background-card transition-colors data-[placeholder]:text-main-foreground-placeholder',
  {
    variants: {
      variant: {
        default: '',
        secondary: cn(
          'bg-main-background-interactive-secondary-default',
          'hover:bg-main-background-interactive-secondary-hover',
          'active:bg-main-background-interactive-secondary-pressed',
          'disabled:bg-main-background-interactive-secondary-disabled',
          'disabled:text-main-foreground-disabled',
          'border-none',
        ),
        button: cn(
          'text-main-foreground-primary',
          'hover:bg-palette-opacity-gray-light',
          'disabled:text-main-foreground-disabled',
          'active:bg-palette-opacity-gray-lighter',
          'border-none bg-transparent',
        ),
      },
      state: {
        default:
          'border-main-border-input hover:border-main-border-bold focus:border-main-border-focus focus:ring-offset focus:ring-main-border-focus focus-visible:border-main-border-focus focus-visible:ring-offset focus-visible:ring-main-border-focus',
        error:
          'border-main-border-error focus:border-main-border-error focus:ring-offset focus:ring-main-border-error focus-visible:border-main-border-error focus-visible:ring-offset focus-visible:ring-main-border-error',
        open: 'border-main-border-focus ring-offset ring-main-border-focus',
      },
      inputSize: {
        sm: 'h-6',
        default: 'h-8',
        large: 'h-10',
      },
      disabled: {
        true: 'border-none bg-main-background-interactive-secondary-disabled text-main-foreground-disabled cursor-not-allowed',
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'secondary',
        state: 'default',
        className: 'border-none hover:border-none focus:border-none focus-visible:border-none',
      },
      {
        variant: 'secondary',
        state: 'error',
        className: 'border-none hover:border-none focus:border-none focus-visible:border-none',
      },
      {
        variant: 'secondary',
        state: 'open',
        className: 'border-none',
      },
    ],
    defaultVariants: {
      variant: 'default',
      state: 'default',
      inputSize: 'default',
      disabled: false,
    },
  },
)

export interface SelectProps extends React.ComponentPropsWithRef<typeof SelectPrimitive.Root> {
  multi?: boolean
  selectedItems?: string[]
  clearable?: boolean
  error?: boolean
  iconLeft?: React.ReactNode
  inputSize?: 'default' | 'large'
  placeholder?: string
  options?: SelectOption[]
  onMultiValueChange?: (values: string[]) => void
}

const Select = React.forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      multi,
      selectedItems = [],
      onMultiValueChange,
      onValueChange,
      clearable = false,
      error = false,
      iconLeft,
      inputSize = 'default',
      placeholder,
      options = [],
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const currentSelectedItems = selectedItems

    const handleValueChange = (value: string) => {
      if (multi && onMultiValueChange) {
        const newSelectedItems = currentSelectedItems.includes(value)
          ? currentSelectedItems.filter((item) => item !== value)
          : [...currentSelectedItems, value]

        onMultiValueChange(newSelectedItems)
      } else if (onValueChange) {
        onValueChange(value)
      }
    }

    const handleTagRemove = (id: string) => {
      const newSelectedItems = currentSelectedItems.filter((item) => item !== id)
      onMultiValueChange?.(newSelectedItems)
    }

    const handleClear = () => {
      if (multi) {
        onMultiValueChange?.([])
      } else {
        onValueChange?.('')
      }
    }

    const contextValue: SelectContextValue = {
      multi,
      selectedItems: currentSelectedItems,
      onMultiValueChange,
      onTagRemove: handleTagRemove,
      onClear: handleClear,
      clearable,
      error,
      disabled,
      iconLeft,
      inputSize,
      placeholder,
      options,
    }

    return (
      <SelectContext.Provider value={contextValue}>
        <div ref={ref}>
          <SelectPrimitive.Root onValueChange={handleValueChange} disabled={disabled} {...props}>
            {children}
          </SelectPrimitive.Root>
        </div>
      </SelectContext.Provider>
    )
  },
)
Select.displayName = 'Select'

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

interface SelectTriggerProps
  extends
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
    VariantProps<typeof selectTriggerVariants> {
  iconLeft?: React.ReactNode
  clearable?: boolean
  error?: boolean
  disabled?: boolean
  inputSize?: 'default' | 'large' | 'sm'
  placeholder?: string
  multiTagsVariant?: MultiSelectTriggerProps['tagsVariant']
  // In multi-select, how many tags can be displayed before the "+" button is shown
  multiMaxDisplay?: number
}

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(
  (
    {
      children,
      iconLeft: propIconLeft,
      clearable: propClearable,
      error: propError,
      disabled: propDisabled,
      inputSize: propInputSize,
      placeholder: propPlaceholder,
      variant,
      className,
      multiTagsVariant,
      multiMaxDisplay,
      ...props
    },
    ref,
  ) => {
    const context = React.useContext(SelectContext)

    // Use prop values if provided, otherwise fall back to context
    const iconLeft = propIconLeft ?? context?.iconLeft
    const clearable = propClearable ?? context?.clearable ?? false
    const error = propError ?? context?.error ?? false
    const disabled = propDisabled ?? context?.disabled ?? false
    const inputSize = propInputSize ?? context?.inputSize ?? 'default'
    const placeholder = propPlaceholder ?? context?.placeholder

    // For multi-select, we need to handle the trigger differently
    if (context?.multi) {
      return (
        <MultiSelectTrigger
          ref={ref}
          className={className}
          variant={variant ?? undefined}
          tagsVariant={multiTagsVariant}
          maxDisplay={multiMaxDisplay}
          {...props}
        />
      )
    }

    const hasValue = Boolean(children) || Boolean(React.Children.count(children))

    return (
      <SelectPrimitive.Trigger
        ref={ref}
        disabled={disabled}
        aria-invalid={error}
        className={cn(
          selectTriggerVariants({
            variant,
            state: error ? 'error' : 'default',
            inputSize,
            disabled,
          }),
          iconLeft ? 'pl-2' : '',
          variant !== 'secondary' &&
            'data-[state=open]:ring-offset data-[state=open]:border-main-border-focus data-[state=open]:ring-main-border-focus',
          className,
        )}
        {...props}
      >
        {iconLeft && <span className='mr-2 flex items-center'>{iconLeft}</span>}
        {children ? (
          children
        ) : (
          <SelectPrimitive.Value
            className={cn(
              'flex-1 truncate text-left placeholder:text-main-foreground-placeholder',
              !hasValue && 'text-main-foreground-placeholder',
            )}
            placeholder={placeholder}
          />
        )}
        {clearable && hasValue && context?.onClear && !disabled && (
          <button
            type='button'
            tabIndex={-1}
            aria-label='Clear selection'
            onClick={(e) => {
              e.stopPropagation()
              context.onClear?.()
            }}
            className='ml-2 flex size-4 items-center justify-center rounded-full bg-main-background-interactive-subtle-default text-main-foreground-inverse focus:outline-none'
          >
            <X size={12} />
          </button>
        )}
        <SelectPrimitive.Icon className='ml-auto shrink-0'>
          <svg width='16' height='16' fill='none' viewBox='0 0 16 16'>
            <path
              d='M4 6l4 4 4-4'
              stroke='var(--foreground-primary)'
              strokeWidth='1.5'
              strokeLinecap='round'
            />
          </svg>
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
    )
  },
)
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

function getTagMaxWidth({
  selectedCount,
  clearable,
}: {
  selectedCount: number
  clearable: boolean
}): string {
  if (selectedCount <= 1) return clearable ? '!max-w-32' : '!max-w-36'
  if (selectedCount === 2) return clearable ? '!max-w-20' : '!max-w-24'
  return clearable ? '!max-w-16' : '!max-w-20'
}

type MultiSelectTriggerProps = React.ComponentPropsWithoutRef<'button'> &
  VariantProps<typeof selectTriggerVariants> & {
    tagsVariant?: Tag['variant']
    maxDisplay?: number
  }

// Multi-select trigger component using Popover
const MultiSelectTrigger = React.forwardRef<HTMLButtonElement, MultiSelectTriggerProps>(
  ({ className, variant, tagsVariant, maxDisplay = 2, ...props }, ref) => {
    const context = React.useContext(SelectContext)
    const [open, setOpen] = React.useState(false)

    if (!context) {
      throw new Error('MultiSelectTrigger must be used within a Select component')
    }

    const {
      selectedItems = [],
      clearable = false,
      error = false,
      disabled = false,
      inputSize = 'default',
      placeholder = 'Select items...',
      options = [],
      onMultiValueChange,
      onTagRemove,
      onClear,
    } = context

    const handleSelect = (optionValue: string) => {
      const newSelectedItems = selectedItems.includes(optionValue)
        ? selectedItems.filter((item) => item !== optionValue)
        : [...selectedItems, optionValue]
      onMultiValueChange?.(newSelectedItems)
    }

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation()
      onClear?.()
    }

    const handleTagRemove = (tagId: string) => {
      onTagRemove?.(tagId)
    }

    const isSelected = (optionValue: string) => selectedItems.includes(optionValue)
    const hasValue = selectedItems.length > 0

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant={
              variant === 'button' ? 'ghost' : variant === 'secondary' ? 'secondary' : 'outline'
            }
            size='sm'
            role='combobox'
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              selectTriggerVariants({
                variant,
                state: error ? 'error' : 'default',
                inputSize,
                disabled,
              }),
              'justify-between',
              open && !error && 'ring-offset border-main-border-focus ring-main-border-focus',
              open && error && 'ring-offset border-main-border-error ring-main-border-error',
              className,
            )}
            {...props}
          >
            <div className='flex min-h-6 min-w-0 flex-1 items-center overflow-hidden'>
              {selectedItems.length === 0 && (
                <span className='min-w-0 truncate text-main-foreground-placeholder text-body-default'>
                  {placeholder}
                </span>
              )}
              <TagsList
                tags={selectedItems.slice(0, maxDisplay).map((val) => {
                  const option = options.find((opt) => opt.value === val)
                  return { id: val, name: option?.label || val, variant: tagsVariant }
                })}
                maxDisplay={Infinity}
                onRemove={
                  disabled
                    ? undefined
                    : (e: React.MouseEvent<HTMLButtonElement>, tagId: string) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleTagRemove(tagId)
                      }
                }
                disabled={disabled}
                className='min-w-0 flex-nowrap'
                tagClassName={getTagMaxWidth({
                  selectedCount: selectedItems.length,
                  clearable,
                })}
              />
            </div>
            {selectedItems.length > maxDisplay && (
              <div
                className='ml-1 flex h-6 shrink-0 items-center rounded-full bg-main-background-interactive-secondary-default px-2 text-main-foreground-secondary !text-label-small'
                data-testid='tags-overflow-indicator'
              >
                {`+${selectedItems.length - maxDisplay}`}
              </div>
            )}
            {clearable && hasValue && !disabled && (
              <button
                type='button'
                tabIndex={-1}
                aria-label='Clear selection'
                onClick={handleClear}
                className='ml-2 flex size-4 items-center justify-center rounded-full bg-main-background-interactive-subtle-default text-main-foreground-inverse hover:bg-main-background-interactive-subtle-hover focus:outline-none'
              >
                <X size={12} />
              </button>
            )}
            <ChevronDown className='ml-auto size-4 shrink-0 opacity-50' />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className='w-[var(--radix-popover-trigger-width)] rounded border-palette-zinc-200 p-0'
          align='start'
        >
          <Command>
            <CommandList>
              <CommandGroup className='max-h-64 overflow-y-auto'>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    onMouseUp={() => !option.disabled && handleSelect(option.value)}
                    className='cursor-pointer text-body-default'
                  >
                    <CheckboxWithLabel
                      disabled={option.disabled}
                      checked={isSelected(option.value)}
                      className='text-label-default'
                    >
                      {option.label}
                    </CheckboxWithLabel>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  },
)
MultiSelectTrigger.displayName = 'MultiSelectTrigger'

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    viewportClassName?: string
  }
>(({ className, children, position = 'popper', viewportClassName, ...props }, ref) => {
  const context = React.useContext(SelectContext)

  // For multi-select, we don't render the SelectContent since we use Popover
  if (context?.multi) {
    return null
  }

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          'relative z-max min-w-32 rounded border border-palette-zinc-200 bg-popover text-main-foreground-primary shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className,
        )}
        position={position}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={cn(
            'max-h-[var(--radix-select-content-available-height)] overflow-y-auto p-1',
            position === 'popper' && 'w-full min-w-[var(--radix-select-trigger-width)]',
            viewportClassName,
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
})
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      'py-1.5 pl-4 pr-2 pt-3 text-main-foreground-tertiary text-label-small',
      className,
    )}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    description?: React.ReactNode
    // isPotentialSelection is used to indicate that this option has been selected by the user
    // but not yet saved in the backend.
    isPotentialSelection?: boolean
  }
>(({ className, children, description, isPotentialSelection, disabled, ...props }, ref) => {
  const context = React.useContext(SelectContext)

  return (
    <SelectPrimitive.Item
      ref={ref}
      disabled={disabled}
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center justify-between gap-2 rounded px-2 py-1.5 text-main-foreground-primary outline-none text-body-default hover:!bg-main-background-interactive-list-hover focus:bg-accent data-[disabled]:pointer-events-none data-[state=checked]:bg-main-background-subtle-semantic-accent data-[disabled]:opacity-50 data-[state=checked]:hover:!bg-main-background-subtle-semantic-accent',
        className,
        { 'text-main-foreground-disabled hover:!bg-none': disabled },
        {
          'bg-main-background-interactive-list-selected hover:!bg-main-background-interactive-list-selectedHover':
            isPotentialSelection,
        },
      )}
      onSelect={(e) => {
        if (context?.multi) {
          // For multi-select, prevent the default closing behavior
          e.preventDefault()
        }
      }}
      {...props}
    >
      <span className={cn('flex w-full items-center gap-3')}>
        <SelectPrimitive.ItemText asChild={React.isValidElement(children)}>
          {children}
        </SelectPrimitive.ItemText>
        {description}
      </span>
      <span className='size-4 text-main-foreground-accent'>
        {isPotentialSelection ? (
          <Check className='size-4 text-main-foreground-accent' />
        ) : (
          <SelectPrimitive.ItemIndicator>
            <Check className='size-4 text-main-foreground-accent' />
          </SelectPrimitive.ItemIndicator>
        )}
      </span>
    </SelectPrimitive.Item>
  )
})
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-muted', className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  type SelectOption,
}
